import RefreshIcon from '@mui/icons-material/Refresh';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { Box, Button } from '@mui/material';
import React from 'react';
import './index.scss';

interface Props {
  children: React.ReactNode;
  /** Names what failed, e.g. "the dashboard". Used in the message. */
  label?: string;
}

interface State {
  error: Error | null;
}

/**
 * Keeps one broken panel from taking the console with it.
 *
 * React unmounts the whole tree on an unhandled render error, so before this
 * existed a single bad payload rendered a blank page — no sidebar, no topbar,
 * no way back. It has happened twice: `stats.activeJobs` on a partial dashboard
 * response, and `entries.filter` when the queue endpoint answered with an
 * object. Both were one field deep and neither should have cost the shell.
 *
 * The boundary goes inside the shell, around the routed page, so navigation
 * survives whatever the page did.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // No reporting pipeline in the console yet; the console trace is what a
    // developer has, so make sure it survives the boundary.
    console.error('Unhandled render error', error, info.componentStack);
  }

  /** Reset on retry so the same subtree gets a second chance to render. */
  private retry = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Box className="cc-boundary" role="alert">
        <ReportProblemOutlinedIcon className="cc-boundary__icon" />
        <h2 className="cc-boundary__title">
          Something went wrong loading {this.props.label ?? 'this page'}.
        </h2>
        <p className="cc-boundary__text">
          The rest of the console still works — use the navigation to move on, or
          try this page again.
        </p>
        <code className="cc-boundary__detail">{error.message}</code>
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.retry}>
          Try again
        </Button>
      </Box>
    );
  }
}

export default ErrorBoundary;
